import { useTranslation } from 'react-i18next'

import { Badge } from '@/components/ui/badge'
import { usePaymentFlowFeatures } from '@/lib/api/paymentFlows'

import { describeRoutingPin, routingFromConfig } from './processor-routing'

type Props = {
    /** A processorConfigs entry. `routing` is absent from the tRPC output type but present at runtime. */
    config: unknown
    pickProcessorStrategy: string | null | undefined
}

/**
 * Read-only recap of a processor's routing pins on the flow detail pages. Rendered only when the
 * pins can actually route: the engine skips them on any strategy other than `weighted`, so showing
 * them elsewhere would claim a routing that does not happen.
 */
export const RoutingPinsSummary = ({ config, pickProcessorStrategy }: Props) => {
    const { t } = useTranslation()
    const { data: features } = usePaymentFlowFeatures()

    if (features?.routingPinsEnabled !== true || pickProcessorStrategy !== 'weighted') {
        return null
    }

    const conditions = (routingFromConfig(config).pins ?? [])
        .map((pin) => describeRoutingPin(pin, t))
        .filter((parts) => parts.length > 0)

    if (conditions.length === 0) {
        return null
    }

    return (
        <div className="mt-1 flex flex-wrap items-center gap-1">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                {t('paymentFlows:form.fields.routing.summary_label')}
            </span>
            {conditions.map((parts, index) => (
                <Badge key={index} variant="outline" className="text-[10px] font-normal">
                    {parts.join(' + ')}
                </Badge>
            ))}
        </div>
    )
}
